import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import CaseAutocomplete from '../Common/CaseAutocomplete';
import { getAuthToken } from '../../lib/authToken';

export interface ImportTemplateRef {
  id: string;
  title: string;
  description?: string;
  documentType?: string;
}

interface ImportTemplateToCaseDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, user picks a case for this template (Firm Templates page). */
  template?: ImportTemplateRef | null;
  /** When set, user picks a template for this case (Case Folder). */
  caseId?: string | null;
  onSuccess?: () => void;
}

const ImportTemplateToCaseDialog: React.FC<ImportTemplateToCaseDialogProps> = ({
  open,
  onClose,
  template = null,
  caseId = null,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const pickCaseMode = Boolean(template?.id);
  const pickTemplateMode = Boolean(caseId) && !pickCaseMode;

  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedCaseId('');
      setSelectedTemplateId('');
    }
  }, [open]);

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['templates-for-import'],
    enabled: open && pickTemplateMode,
    queryFn: async (): Promise<ImportTemplateRef[]> => {
      const token = getAuthToken();
      const response = await fetch('/api/documents?isTemplate=true&limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      const json = await response.json();
      return (json.data?.documents || []) as ImportTemplateRef[];
    },
  });

  const importMutation = useMutation({
    mutationFn: async ({ templateId, targetCaseId }: { templateId: string; targetCaseId: string }) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/case-documents/${targetCaseId}/documents/import-template`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateId }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to import template.'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-count'] });
      if (caseId) {
        queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
      }
      toast.success('Template imported — case document created with placeholders filled');
      onSuccess?.();
      onClose();
    },
    onError: (error: Error) => {
      console.error('Import template error:', error);
      toast.error(toUserErrorMessage(error, 'Failed to import template.'));
    },
  });

  const canSubmit = pickCaseMode
    ? Boolean(template?.id && selectedCaseId)
    : Boolean(caseId && selectedTemplateId);

  const handleImport = () => {
    if (pickCaseMode && template?.id && selectedCaseId) {
      importMutation.mutate({ templateId: template.id, targetCaseId: selectedCaseId });
      return;
    }
    if (pickTemplateMode && caseId && selectedTemplateId) {
      importMutation.mutate({ templateId: selectedTemplateId, targetCaseId: caseId });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={pickTemplateMode ? 'md' : 'sm'} fullWidth>
      <DialogTitle>
        {pickTemplateMode ? 'Import Template to Case Folder' : 'Import Template to Case'}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          A new case document is created from this library template. Placeholders such as
          {' '}{'{{Client_FirstName}}'} and {'{{Date_of_Signing}}'} are filled from the case
          file. The library template itself is left unchanged.
        </Alert>
        {pickCaseMode && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {template?.title || 'Selected template'}
            </Typography>
            <CaseAutocomplete
              label="Case"
              required
              value={selectedCaseId || null}
              onChange={(c) => setSelectedCaseId(c?.id || '')}
            />
          </>
        )}

        {pickTemplateMode && (
          <>
            {templatesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Alert severity="info">
                No templates available. Create templates in the Firm Templates section first.
              </Alert>
            ) : (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {templates.map((t) => (
                  <Grid item xs={12} md={6} key={t.id}>
                    <Card
                      variant={selectedTemplateId === t.id ? 'outlined' : 'elevation'}
                      sx={{
                        borderColor: selectedTemplateId === t.id ? 'primary.main' : undefined,
                        borderWidth: selectedTemplateId === t.id ? 2 : undefined,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Description sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle1">{t.title}</Typography>
                        </Box>
                        {t.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t.description}
                          </Typography>
                        )}
                        {t.documentType && (
                          <Chip
                            label={t.documentType.replace(/_/g, ' ')}
                            size="small"
                            color="secondary"
                            sx={{ mr: 1 }}
                          />
                        )}
                        <Button
                          size="small"
                          variant={selectedTemplateId === t.id ? 'contained' : 'outlined'}
                          onClick={() => setSelectedTemplateId(t.id)}
                          sx={{ mt: 1 }}
                        >
                          {selectedTemplateId === t.id ? 'Selected' : 'Select'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || importMutation.isPending}
          onClick={handleImport}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportTemplateToCaseDialog;
